import { useUploadDataRoomFiles } from "@/services/api/dataRoomService"
import type { DataRoomFile, DataRoomTree } from "@/types/dataRoom"
import { useCallback, useEffect, useRef, useState } from "react"

export type DataRoomUploadEntryStatus = "uploading" | "uploaded" | "failed"

export interface DataRoomUploadEntry {
  id: string
  fileName: string
  file: File
  /** The folder this file was targeted at when the upload started — carried
   * on the entry itself (not read from the hook's live `folderId` param) so
   * a retry after the user has since navigated to a different folder still
   * lands where it was originally dropped, instead of being silently
   * misfiled into whatever folder happens to be selected now. */
  folderId: string | null
  /** Set when this upload is a revision of an existing file. */
  supersedesFileId?: string
  status: DataRoomUploadEntryStatus
  /** Upload progress, 0..100. Driven by axios' `onUploadProgress` for this
   * file's own request (one request per file — see `CONCURRENCY` below), so
   * it maps directly to that file's bytes, not a shared batch total. Frozen
   * at its last value whenever the browser can't report a byte total, and
   * forced to 100 the moment the entry reconciles into "uploaded". */
  progress: number
  errorCode?: string
  retryable: boolean
  /** Set the moment this entry reconciles into "uploaded" (first success or
   * a later network-error reconciliation) — the auto-dismiss timer below is
   * keyed on `id` + this timestamp, not re-derived from the whole `entries`
   * array, so an unrelated entry appearing/changing never resets it. */
  reconciledAt?: number
  /** The server row this entry resolved to: the upload response's row, or
   * the row a reconciliation matched. The list hides that persisted row for
   * as long as this entry still lingers, so a finished upload never shows
   * twice at once. */
  serverFileId?: string
  /** File ids that existed in the tree the moment this entry was FIRST
   * submitted — frozen at creation, never recomputed. Reconciliation (after a
   * network error, or on manual retry) treats a same-name/same-size file in
   * the target folder as proof THIS upload actually landed only if its id is
   * NOT in this set; a file already sitting there before we ever tried must
   * never be mistaken for this upload having succeeded. A retry's own
   * refetch would otherwise have already pulled a just-landed file into
   * "known", making every later retry blind to it — so this snapshot has to
   * survive across retries, not just across one submission.
   */
  preExistingFileIds: string[]
}

interface SubmissionItem {
  id: string
  fileName: string
  file: File
  folderId: string | null
  supersedesFileId?: string
}

export interface HandleFilesOptions {
  /** Target folder for this batch; defaults to the folder in view. */
  folderId?: string | null
  /** Upload a single file as the revision of an existing one. */
  supersedesFileId?: string
}

// Auto-dismiss a reconciled "uploaded" row after it has had a moment to
// register as a success — the real row already lives in the table below.
const UPLOADED_ROW_LINGER_MS = 2000

// One request per file — the server's batch endpoint still accepts a
// `files` array, but sending a single-file "batch" gives each file its own
// response and its own `onUploadProgress` byte stream, instead of a shared
// total across several unrelated files. `CONCURRENCY` caps how many of
// those single-file requests run at once, so dropping 30 files doesn't fire
// 30 simultaneous uploads.
const CONCURRENCY = 3

interface Semaphore {
  acquire: () => Promise<void>
  release: () => void
}

/**
 * A counting semaphore capping how many holders can be active at once.
 * `submitOne` acquires one permit before its actual upload request and
 * releases it right after — the ONLY place any upload request is made, for
 * both a batch submission and a manual retry — so `CONCURRENCY` in-flight
 * uploads is a single global cap shared by every entry point, not a limit
 * `submit`'s own loop happens to enforce on itself. A retry can't sneak
 * past it just because it didn't come from `submit`'s loop.
 */
function createSemaphore(limit: number): Semaphore {
  let active = 0
  const queue: Array<() => void> = []

  return {
    acquire: () => {
      if (active < limit) {
        active++
        return Promise.resolve()
      }
      return new Promise((resolve) => {
        queue.push(() => {
          active++
          resolve()
        })
      })
    },
    release: () => {
      active--
      queue.shift()?.()
    },
  }
}

/** A server row's claim: which entry it's attributed to, and whether that
 * attribution came from an authoritative source (this entry's OWN success
 * response naming that row) or merely from reconciliation (a fresh tree
 * fetch guessing, by name+size+folder, that an unrelated row is this
 * entry's). An authoritative claim always outranks a reconciled one. */
interface FileClaim {
  entryId: string
  authoritative: boolean
}

function findLandedFile(
  files: DataRoomFile[],
  target: { fileName: string; size: number; folderId: string | null },
  preExistingFileIds: string[],
  claims: Map<string, FileClaim>,
): DataRoomFile | undefined {
  // `claims` holds every server row some entry of this hook has already
  // been matched to (a successful upload's returned row, or an earlier
  // reconciliation). Two entries with the same name and size, one landed
  // and one lost, must not both claim the single row that landed.
  return files.find(
    (f) =>
      !preExistingFileIds.includes(f.id) &&
      !claims.has(f.id) &&
      f.fileName === target.fileName &&
      f.fileSizeBytes === target.size &&
      (f.folderId ?? null) === (target.folderId ?? null),
  )
}

/**
 * Records that `entryId` claims server row `fileId`. If another entry
 * already holds that row's claim, an authoritative claim (this entry's own
 * success response) overrides a merely reconciled one (a guess from a
 * fresh tree fetch) — the response naming the row IS the source of truth,
 * so it wins even if reconciliation guessed first. Returns the id of the
 * entry that just lost its claim, if any, so the caller can flip it back
 * to failed/retryable instead of leaving a false "uploaded" success on the
 * table.
 */
function claimFile(
  claims: Map<string, FileClaim>,
  fileId: string,
  entryId: string,
  authoritative: boolean,
): string | undefined {
  const existing = claims.get(fileId)
  if (!existing || existing.entryId === entryId) {
    claims.set(fileId, {
      entryId,
      authoritative: authoritative || existing?.authoritative === true,
    })
    return undefined
  }
  if (authoritative && !existing.authoritative) {
    claims.set(fileId, { entryId, authoritative: true })
    return existing.entryId
  }
  // A reconciled claim never displaces another claim of any kind — it only
  // takes an unclaimed row (see `findLandedFile` above).
  return undefined
}

/** The shape a bumped entry takes when an authoritative response overrides
 * its earlier (wrong) reconciliation — shared by the in-place update and by
 * reviving an entry that had already auto-dismissed (see
 * `dismissedUploadedEntriesRef`). */
function toBumpedFailedEntry(entry: DataRoomUploadEntry): DataRoomUploadEntry {
  return {
    ...entry,
    status: "failed",
    errorCode: "UPLOAD_FAILED",
    retryable: true,
    reconciledAt: undefined,
  }
}

/**
 * Optimistic upload tracker for the data room, with real per-file byte
 * progress.
 *
 * Every file gets its own request (the server's batch endpoint is called
 * with a single-file `files` array), capped at `CONCURRENCY` in flight at
 * once by a shared semaphore inside `submitOne` — see `createSemaphore`.
 * That cap is global to the hook instance, not per call site, so a manual
 * `retry` shares it with whatever batch is still uploading instead of
 * sneaking past it. One request per file means one `onUploadProgress`
 * stream per file, so `entry.progress` tracks that file's own bytes instead
 * of a shared total across an unrelated batch. `useUploadDataRoomFiles`
 * posts each request and the server always answers 201 with
 * `{uploaded, failed}` — never a per-file HTTP error — so a SUCCESSFUL
 * response tells us whether this one file failed by name.
 *
 * A transport failure (the `catch`, not a business failure in `failed[]`) is
 * ambiguous: the request may have died before or after the server committed
 * the file. Before giving up and marking the row retryable, it is
 * reconciled against a fresh fetch of the tree (`refetchTree`) —
 * `findLandedFile` — so a file that actually landed shows as uploaded
 * instead of inviting a duplicate re-upload. The same reconciliation runs
 * again on manual retry, since more time may have passed since the failure.
 *
 * Matching a request to its response row is done by file name — safe here
 * since a request now carries exactly one file.
 */
export function useDataRoomUpload(
  folderId: string | null,
  knownFiles: DataRoomFile[],
  refetchTree: () => Promise<{ data?: DataRoomTree } | undefined>,
) {
  const [entries, setEntries] = useState<DataRoomUploadEntry[]>([])
  const nextIdRef = useRef(0)
  const { mutateAsync } = useUploadDataRoomFiles()
  // Stable for the component's lifetime — every call to `submitOne`, from
  // `submit` or from `retry`, acquires/releases the SAME semaphore.
  const semaphoreRef = useRef(createSemaphore(CONCURRENCY))
  // Server rows already accounted for by an entry of this hook; see
  // `findLandedFile` and `claimFile`.
  const claimedFileIdsRef = useRef<Map<string, FileClaim>>(new Map())
  // Snapshots of "uploaded" entries the instant BEFORE the auto-dismiss
  // timer below removes them from `entries` — kept only long enough for a
  // still-outstanding claim on that row to reference them. If an
  // authoritative response later bumps one of these ids (see
  // `claimFile`/`toBumpedFailedEntry`), it's revived from here instead of
  // the conflict signal being silently lost because its row was already
  // gone from the table.
  const dismissedUploadedEntriesRef = useRef<Map<string, DataRoomUploadEntry>>(
    new Map(),
  )

  // XHR fires `onUploadProgress` many times a second. Creating a fresh
  // entry object on every tick, even when the rounded percent hasn't
  // actually moved, would make that row re-render at that rate for no
  // visible change. Bailing out to the SAME entry object (inside a still-
  // fresh array) when the percent is unchanged keeps that row's identity
  // stable across those ticks, so it skips re-rendering even though the
  // parent list re-renders around it.
  const setProgress = useCallback((entryId: string, percent: number) => {
    setEntries((prev) => {
      const index = prev.findIndex((entry) => entry.id === entryId)
      if (index === -1) return prev
      if (prev[index].progress === percent) return prev
      const next = prev.slice()
      next[index] = { ...prev[index], progress: percent }
      return next
    })
  }, [])

  const submitOne = useCallback(
    async (item: SubmissionItem, preExistingFileIds: string[]) => {
      try {
        // The semaphore permit only guards the actual network request — a
        // slot frees up the moment this file's upload settles (either way),
        // not only once its (unrelated) failure reconciliation below has
        // also finished.
        let data: Awaited<ReturnType<typeof mutateAsync>>
        try {
          await semaphoreRef.current.acquire()
          data = await mutateAsync({
            files: [item.file],
            folderId: item.folderId,
            supersedesFileId: item.supersedesFileId,
            onProgress: (fraction) => {
              const percent = Math.min(
                100,
                Math.max(0, Math.round(fraction * 100)),
              )
              setProgress(item.id, percent)
            },
          })
        } finally {
          semaphoreRef.current.release()
        }
        const failure = data.failed.find((f) => f.fileName === item.fileName)
        // This response is authoritative: it names the exact row THIS
        // request produced. If an unrelated entry had already reconciled
        // itself onto that same row (a guess from a delayed transport
        // failure), that guess just proved wrong — this claim overrides it.
        const bumpedEntryIds = new Set<string>()
        for (const uploaded of data.uploaded) {
          const bumped = claimFile(
            claimedFileIdsRef.current,
            uploaded.id,
            item.id,
            true,
          )
          if (bumped) bumpedEntryIds.add(bumped)
        }
        setEntries((prev) => {
          // Its reconciled "uploaded" claim on this row just got overridden
          // by the authoritative response above — the row actually belongs
          // to `item`, so this entry's own content never landed. Flip it
          // back to failed/retryable instead of leaving a false success on
          // the table.
          const foundBumpedIds = new Set<string>()
          const mapped = prev.map((entry) => {
            if (entry.id === item.id) {
              if (failure) {
                return {
                  ...entry,
                  status: "failed" as const,
                  errorCode: failure.code,
                  retryable: true,
                }
              }
              return {
                ...entry,
                status: "uploaded" as const,
                progress: 100,
                errorCode: undefined,
                retryable: false,
                reconciledAt: Date.now(),
                serverFileId:
                  data.uploaded.find((f) => f.fileName === item.fileName)?.id ??
                  entry.serverFileId,
              }
            }
            if (bumpedEntryIds.has(entry.id)) {
              foundBumpedIds.add(entry.id)
              return toBumpedFailedEntry(entry)
            }
            return entry
          })

          // A bumped entry may already have auto-dismissed — its row gone
          // from `prev` — before this authoritative response arrived.
          // Revive it from the pre-dismiss snapshot rather than silently
          // losing the conflict signal (and the ability to retry it).
          const revived: DataRoomUploadEntry[] = []
          for (const bumpedId of bumpedEntryIds) {
            if (foundBumpedIds.has(bumpedId)) continue
            const dismissedEntry =
              dismissedUploadedEntriesRef.current.get(bumpedId)
            if (!dismissedEntry) continue
            dismissedUploadedEntriesRef.current.delete(bumpedId)
            revived.push(toBumpedFailedEntry(dismissedEntry))
          }

          return revived.length ? [...mapped, ...revived] : mapped
        })
      } catch {
        // Transport failure — the request may have landed server-side before
        // the connection dropped. Reconcile against a fresh tree fetch before
        // giving up on it.
        const result = await refetchTree()
        const freshFiles = result?.data?.files ?? []
        const landed = findLandedFile(
          freshFiles,
          {
            fileName: item.fileName,
            size: item.file.size,
            folderId: item.folderId,
          },
          preExistingFileIds,
          claimedFileIdsRef.current,
        )
        // This is a guess, not proof — mark it non-authoritative so a later
        // authoritative response for the same row (the original request's
        // own delayed success) can still override it. See `claimFile`.
        if (landed) {
          claimFile(claimedFileIdsRef.current, landed.id, item.id, false)
        }
        setEntries((prev) =>
          prev.map((entry) => {
            if (entry.id !== item.id) return entry
            if (landed) {
              return {
                ...entry,
                status: "uploaded",
                progress: 100,
                errorCode: undefined,
                retryable: false,
                reconciledAt: Date.now(),
                serverFileId: landed.id,
              }
            }
            return {
              ...entry,
              status: "failed",
              errorCode: "UPLOAD_FAILED",
              retryable: true,
            }
          }),
        )
      }
    },
    [mutateAsync, refetchTree, setProgress],
  )

  const submit = useCallback(
    (items: SubmissionItem[]) => {
      const preExistingFileIds = knownFiles.map((f) => f.id)
      // Fire-and-forget: `handleFiles` is a synchronous UI handler, so this
      // runs in the background. Every item is started right away —
      // `CONCURRENCY` in-flight uploads is enforced by the shared semaphore
      // inside `submitOne` itself (see above), not by how many of these
      // calls happen to be outstanding, so a failing or slow file only
      // holds up its own request, and `retry` sharing that same semaphore
      // can never push the total past the cap either.
      for (const item of items) {
        void submitOne(item, preExistingFileIds)
      }
    },
    [submitOne, knownFiles],
  )

  const handleFiles = useCallback(
    (files: File[], options?: HandleFilesOptions) => {
      if (!files.length) return
      const preExistingFileIds = knownFiles.map((f) => f.id)
      const targetFolderId =
        options?.folderId !== undefined ? options.folderId : folderId
      const items: SubmissionItem[] = files.map((file) => ({
        id: `data-room-upload-${nextIdRef.current++}`,
        fileName: file.name,
        file,
        folderId: targetFolderId,
        supersedesFileId: options?.supersedesFileId,
      }))
      setEntries((prev) => [
        ...prev,
        ...items.map(
          (item): DataRoomUploadEntry => ({
            id: item.id,
            fileName: item.fileName,
            file: item.file,
            folderId: item.folderId,
            supersedesFileId: item.supersedesFileId,
            status: "uploading",
            progress: 0,
            retryable: false,
            preExistingFileIds,
          }),
        ),
      ])
      submit(items)
    },
    [submit, folderId, knownFiles],
  )

  // The async continuation of a retry, run AFTER the entry has already been
  // flipped to "uploading" (see `retry` below). Reconciles against a fresh
  // tree fetch first — more time may have passed since the original
  // failure — then falls through to `submitOne`, which shares the same
  // `CONCURRENCY` semaphore as every other upload, so a retry can never
  // push the total in-flight count past the cap.
  const retryOne = useCallback(
    async (entry: DataRoomUploadEntry) => {
      const result = await refetchTree()
      const freshFiles = result?.data?.files ?? []
      const landed = findLandedFile(
        freshFiles,
        {
          fileName: entry.fileName,
          size: entry.file.size,
          folderId: entry.folderId,
        },
        entry.preExistingFileIds,
        claimedFileIdsRef.current,
      )
      if (landed) {
        // Non-authoritative — a guess from reconciliation, same as above.
        claimFile(claimedFileIdsRef.current, landed.id, entry.id, false)
        setEntries((prev) =>
          prev.map((e) =>
            e.id === entry.id
              ? {
                  ...e,
                  status: "uploaded",
                  progress: 100,
                  errorCode: undefined,
                  retryable: false,
                  reconciledAt: Date.now(),
                  serverFileId: landed.id,
                }
              : e,
          ),
        )
        return
      }

      await submitOne(
        {
          id: entry.id,
          fileName: entry.fileName,
          file: entry.file,
          folderId: entry.folderId,
          supersedesFileId: entry.supersedesFileId,
        },
        entry.preExistingFileIds,
      )
    },
    [refetchTree, submitOne],
  )

  // Entry ids currently mid-retry. A `setState` updater function is NOT
  // guaranteed to run synchronously at the point `setEntries` is called
  // (React may defer it to the next render), so it cannot be used as a
  // double-click guard by itself — reading the flipped status back out of
  // `entries`/`result.current` right after calling `retry` can still see
  // the stale "failed" value. This plain ref, mutated directly and
  // immediately, is the actual guard; `entries` stays purely for display.
  const retryingIdsRef = useRef<Set<string>>(new Set())

  const retry = useCallback(
    (entryId: string): Promise<void> => {
      if (retryingIdsRef.current.has(entryId)) {
        // Already mid-retry — a double click's second call is a no-op.
        return Promise.resolve()
      }
      const entry = entries.find((e) => e.id === entryId)
      if (!entry || entry.status !== "failed") return Promise.resolve()

      // Claim it synchronously, before any `await` — including before the
      // `setEntries` call right below, whose own timing isn't guaranteed.
      retryingIdsRef.current.add(entryId)

      setEntries((prev) =>
        prev.map((e) =>
          e.id === entryId
            ? {
                ...e,
                status: "uploading",
                progress: 0,
                errorCode: undefined,
                retryable: false,
              }
            : e,
        ),
      )

      // Release the claim once this retry fully settles — a retry that
      // fails again must be retryable once more, not silently no-op
      // forever because its id was never freed.
      return retryOne(entry).finally(() => {
        retryingIdsRef.current.delete(entryId)
      })
    },
    [entries, retryOne],
  )

  const dismiss = useCallback((entryId: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== entryId))
  }, [])

  // Auto-dismiss reconciled "uploaded" rows once they've had a moment to
  // register as a success. Timers are keyed on `id:reconciledAt` and tracked
  // in a ref map — scheduled once per key and never rescheduled — so an
  // unrelated entries change (a new file added to the batch, another row
  // reconciling) never resets a timer that is already counting down.
  const dismissTimersRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>(),
  )

  useEffect(() => {
    const timers = dismissTimersRef.current
    const uploaded = entries.filter(
      (e) => e.status === "uploaded" && e.reconciledAt !== undefined,
    )
    const liveKeys = new Set(uploaded.map((e) => `${e.id}:${e.reconciledAt}`))

    for (const [key, timerId] of timers) {
      if (!liveKeys.has(key)) {
        clearTimeout(timerId)
        timers.delete(key)
      }
    }

    for (const entry of uploaded) {
      const key = `${entry.id}:${entry.reconciledAt}`
      if (timers.has(key)) continue
      const timerId = setTimeout(() => {
        timers.delete(key)
        // Snapshot before removing — see `dismissedUploadedEntriesRef`.
        dismissedUploadedEntriesRef.current.set(entry.id, entry)
        dismiss(entry.id)
      }, UPLOADED_ROW_LINGER_MS)
      timers.set(key, timerId)
    }
  }, [entries, dismiss])

  useEffect(() => {
    const timers = dismissTimersRef.current
    return () => {
      timers.forEach(clearTimeout)
      timers.clear()
    }
  }, [])

  return { entries, handleFiles, retry, dismiss }
}
